interface LayoutNode {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface LayoutEdge {
  source: string;
  target: string;
}

/**
 * Simple force-directed layout algorithm.
 * - Edges attract connected nodes
 * - All nodes repel each other (avoid overlap)
 * - Runs for a fixed number of iterations
 */
export function forceDirectedLayout(
  nodes: LayoutNode[],
  edges: LayoutEdge[],
  iterations = 150,
): Record<string, { x: number; y: number }> {
  if (nodes.length === 0) return {};

  // Work with copies
  const pos = nodes.map(n => ({ id: n.id, x: n.x, y: n.y, w: n.width, h: n.height }));

  // Build adjacency with edge weights (multiple edges between same zones = stronger pull)
  const edgeWeights = new Map<string, number>();
  for (const e of edges) {
    if (e.source === e.target) continue;
    const key = [e.source, e.target].sort().join('::');
    edgeWeights.set(key, (edgeWeights.get(key) ?? 0) + 1);
  }

  const REPULSION = 80000;
  const ATTRACTION = 0.002;
  const MIN_DIST = 50;
  const DAMPING = 0.9;
  const INITIAL_TEMP = 10;

  const vx = new Float64Array(pos.length);
  const vy = new Float64Array(pos.length);

  for (let iter = 0; iter < iterations; iter++) {
    const temp = INITIAL_TEMP * (1 - iter / iterations);
    const fx = new Float64Array(pos.length);
    const fy = new Float64Array(pos.length);

    // Repulsion between all pairs
    for (let i = 0; i < pos.length; i++) {
      for (let j = i + 1; j < pos.length; j++) {
        let dx = pos[i].x - pos[j].x;
        let dy = pos[i].y - pos[j].y;
        const dist = Math.max(Math.sqrt(dx * dx + dy * dy), MIN_DIST);

        // Extra repulsion when overlapping
        const overlapX = (pos[i].w + pos[j].w) / 2 + 30;
        const overlapY = (pos[i].h + pos[j].h) / 2 + 30;
        const isOverlapping = Math.abs(dx) < overlapX && Math.abs(dy) < overlapY;

        const force = REPULSION / (dist * dist) * (isOverlapping ? 3 : 1);
        const forceX = (dx / dist) * force;
        const forceY = (dy / dist) * force;

        fx[i] += forceX;
        fy[i] += forceY;
        fx[j] -= forceX;
        fy[j] -= forceY;
      }
    }

    // Attraction along edges
    const idToIdx = new Map(pos.map((p, i) => [p.id, i]));
    for (const [key, weight] of edgeWeights) {
      const [srcId, tgtId] = key.split('::');
      const si = idToIdx.get(srcId);
      const ti = idToIdx.get(tgtId);
      if (si === undefined || ti === undefined) continue;

      const dx = pos[ti].x - pos[si].x;
      const dy = pos[ti].y - pos[si].y;
      const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1);

      // Stronger attraction for more edges between same pair
      const strength = ATTRACTION * Math.log2(weight + 1);
      const forceX = dx * strength;
      const forceY = dy * strength;

      fx[si] += forceX;
      fy[si] += forceY;
      fx[ti] -= forceX;
      fy[ti] -= forceY;
    }

    // Apply forces with velocity damping
    for (let i = 0; i < pos.length; i++) {
      vx[i] = (vx[i] + fx[i]) * DAMPING;
      vy[i] = (vy[i] + fy[i]) * DAMPING;

      // Clamp velocity by temperature
      const speed = Math.sqrt(vx[i] * vx[i] + vy[i] * vy[i]);
      if (speed > temp * 10) {
        const scale = (temp * 10) / speed;
        vx[i] *= scale;
        vy[i] *= scale;
      }

      pos[i].x += vx[i];
      pos[i].y += vy[i];
    }
  }

  // Normalize: shift so top-left is (0, 0)
  let minX = Infinity, minY = Infinity;
  for (const p of pos) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
  }

  const result: Record<string, { x: number; y: number }> = {};
  for (const p of pos) {
    result[p.id] = { x: Math.round(p.x - minX), y: Math.round(p.y - minY) };
  }

  return result;
}
